import React, { useState, useEffect, useRef } from "react";
import { searchUsers, createInviteByEmail, getOrganizationPendingInvites } from "../../api/api";
import { defaultPfpUrl, affiliationRoles } from "../utils";
import { cn } from "../../lib/utils";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";

const inputClass =
  "w-full rounded-md border border-ui-border bg-ui-card px-3 py-2 text-sm text-ui-foreground placeholder:text-ui-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring focus-visible:ring-offset-2 focus-visible:ring-offset-ui-background";

/**
 * @param {string} value
 * @returns {boolean}
 */
function looksLikeEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || "").trim());
}

/**
 * @param {Object} u
 * @returns {string}
 */
function personLabel(u) {
  if (!u) return "User";
  if (u.first_name && u.last_name) {
    return `${u.first_name} ${u.last_name}`.trim();
  }
  if (u.first_name) return u.first_name;
  if (u.last_name) return u.last_name;
  return u.username || "User";
}

/**
 * Multi-step flow: find user, pick role, confirm.
 * @param {Object} props
 * @param {boolean} props.open
 * @param {function(boolean): void} props.onOpenChange
 * @param {number} props.userId
 * @param {Object} props.user
 * @param {function} [props.refreshUser]
 * @returns {JSX.Element|null}
 */
function NewAffiliationModal({ open, onOpenChange, userId, user, refreshUser }) {
  const [step, setStep] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [alreadyAffiliatedName, setAlreadyAffiliatedName] = useState(null);
  const [pendingInviteEmails, setPendingInviteEmails] = useState(() => new Set());

  const [roleSearch, setRoleSearch] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [startDate, setStartDate] = useState("");
  const roleDropdownRef = useRef(null);

  const searchRequestIdRef = useRef(0);
  const emailLookupRequestIdRef = useRef(0);
  const searchQueryRef = useRef(searchQuery);
  searchQueryRef.current = searchQuery;
  const userRef = useRef(user);
  userRef.current = user;
  const pendingInviteEmailsRef = useRef(pendingInviteEmails);
  pendingInviteEmailsRef.current = pendingInviteEmails;
  const selectedUserRef = useRef(selectedUser);
  selectedUserRef.current = selectedUser;

  useEffect(() => {
    if (!open) {
      setStep(1);
      setSelectedUser(null);
      setSearchQuery("");
      setSearchResults([]);
      setError(null);
      setRoleSearch("");
      setSelectedRole("");
      setShowRoleDropdown(false);
      setStartDate("");
      setFetching(false);
      setInviteEmail("");
      setAlreadyAffiliatedName(null);
      setPendingInviteEmails(new Set());
      return;
    }

    let cancelled = false;
    (async () => {
      const result = await getOrganizationPendingInvites(userId);
      if (cancelled) return;
      if (result.error || !result.success) {
        setPendingInviteEmails(new Set());
        return;
      }
      const emails = new Set(
        (result.data || [])
          .map((inv) => (inv.email || "").trim().toLowerCase())
          .filter(Boolean)
      );
      setPendingInviteEmails(emails);
    })();

    return () => {
      cancelled = true;
    };
  }, [open, userId]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (roleDropdownRef.current && !roleDropdownRef.current.contains(event.target)) {
        setShowRoleDropdown(false);
      }
    }
    if (showRoleDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showRoleDropdown]);

  /**
   * @param {Object} matchedUser
   * @returns {boolean}
   */
  const isAlreadyAffiliated = (matchedUser) => {
    if (!matchedUser) return false;
    const email = (matchedUser.email || "").trim().toLowerCase();
    const activeAffiliationUserIds = new Set(
      (userRef.current?.affiliations || [])
        .filter((aff) => aff.status === "active" || !aff.status)
        .map((aff) => aff.user_id)
    );
    if (activeAffiliationUserIds.has(matchedUser.id)) {
      return true;
    }
    if (email && pendingInviteEmailsRef.current.has(email)) {
      return true;
    }
    const activeByEmail = (userRef.current?.affiliations || []).some((aff) => {
      const affEmail = (aff.user?.email || "").trim().toLowerCase();
      return affEmail && email && affEmail === email && (aff.status === "active" || !aff.status);
    });
    return activeByEmail;
  };

  useEffect(() => {
    if (!searchQuery.trim()) {
      searchRequestIdRef.current += 1;
      setSearchResults([]);
      setFetching(false);
      return;
    }
    setFetching(true);
    const requestId = ++searchRequestIdRef.current;
    const trimmed = searchQuery.trim();
    const timeoutId = setTimeout(async () => {
      try {
        const res = await searchUsers(userId, trimmed);
        if (requestId !== searchRequestIdRef.current) {
          return;
        }
        if (trimmed !== searchQueryRef.current.trim()) {
          return;
        }
        if (res.success && res.data) {
          const activeAffiliationUserIds = new Set(
            (userRef.current?.affiliations || [])
              .filter((aff) => aff.status === "active" || !aff.status)
              .map((aff) => aff.user_id)
          );
          const filtered = res.data.filter((item) => {
            if (item.type !== "user" || item.id === userId) return false;
            if (activeAffiliationUserIds.has(item.id)) return false;
            const email = (item.email || "").trim().toLowerCase();
            if (email && pendingInviteEmailsRef.current.has(email)) return false;
            return true;
          });
          setSearchResults(filtered);
        } else {
          setSearchResults([]);
        }
      } catch (err) {
        console.error("Search error:", err);
        if (requestId === searchRequestIdRef.current) {
          setSearchResults([]);
        }
      } finally {
        if (requestId === searchRequestIdRef.current) {
          setFetching(false);
        }
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, userId, pendingInviteEmails]);

  useEffect(() => {
    const trimmed = inviteEmail.trim();
    const selected = selectedUserRef.current;
    const selectedEmail = (selected?.email || "").trim().toLowerCase();

    if (!looksLikeEmail(trimmed)) {
      emailLookupRequestIdRef.current += 1;
      setAlreadyAffiliatedName(null);
      if (selected && selectedEmail !== trimmed.toLowerCase()) {
        setSelectedUser(null);
      }
      return;
    }

    if (selected && selectedEmail === trimmed.toLowerCase()) {
      if (isAlreadyAffiliated(selected)) {
        setSelectedUser(null);
        setAlreadyAffiliatedName(personLabel(selected));
      } else {
        setAlreadyAffiliatedName(null);
      }
      return;
    }

    const requestId = ++emailLookupRequestIdRef.current;
    const timeoutId = setTimeout(async () => {
      try {
        const emailKey = trimmed.toLowerCase();
        if (pendingInviteEmailsRef.current.has(emailKey)) {
          if (requestId !== emailLookupRequestIdRef.current) return;
          setSelectedUser(null);
          const res = await searchUsers(userId, trimmed);
          if (requestId !== emailLookupRequestIdRef.current) return;
          const match =
            res.success && res.data
              ? res.data.find(
                  (item) =>
                    item.type === "user" &&
                    (item.email || "").trim().toLowerCase() === emailKey
                )
              : null;
          setAlreadyAffiliatedName(match ? personLabel(match) : trimmed);
          return;
        }

        const res = await searchUsers(userId, trimmed);
        if (requestId !== emailLookupRequestIdRef.current) {
          return;
        }
        if (inviteEmail.trim().toLowerCase() !== emailKey) {
          return;
        }
        const match =
          res.success && res.data
            ? res.data.find(
                (item) =>
                  item.type === "user" &&
                  (item.email || "").trim().toLowerCase() === emailKey
              )
            : null;

        if (!match) {
          setAlreadyAffiliatedName(null);
          if (selectedUserRef.current && selectedEmail !== emailKey) {
            setSelectedUser(null);
          }
          return;
        }

        if (isAlreadyAffiliated(match)) {
          setSelectedUser(null);
          setAlreadyAffiliatedName(personLabel(match));
          return;
        }

        setAlreadyAffiliatedName(null);
        setSelectedUser(match);
        setSearchQuery("");
        setSearchResults([]);
      } catch (err) {
        console.error("Email lookup error:", err);
        if (requestId === emailLookupRequestIdRef.current) {
          setAlreadyAffiliatedName(null);
        }
      }
    }, 300);

    return () => clearTimeout(timeoutId);
    // isAlreadyAffiliated reads refs updated each render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteEmail, userId, pendingInviteEmails, user]);

  const showInitialSearch = fetching && searchResults.length === 0 && searchQuery.trim();
  const showSearchUpdating = fetching && searchResults.length > 0;

  const handleUserSelect = (u) => {
    if (isAlreadyAffiliated(u)) {
      setSelectedUser(null);
      setInviteEmail((u.email || "").trim());
      setAlreadyAffiliatedName(personLabel(u));
      setSearchQuery("");
      setSearchResults([]);
      return;
    }
    setAlreadyAffiliatedName(null);
    setSelectedUser(u);
    setInviteEmail((u.email || "").trim());
    setSearchQuery("");
    setSearchResults([]);
  };

  const handleClearSelectedUser = () => {
    setSelectedUser(null);
    setInviteEmail("");
    setAlreadyAffiliatedName(null);
  };

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    setRoleSearch(role);
    setShowRoleDropdown(false);
  };

  const handleRoleInputChange = (value) => {
    setRoleSearch(value);
    setSelectedRole(value);
    setShowRoleDropdown(true);
  };

  const filteredRoles = affiliationRoles.filter((role) =>
    role.toLowerCase().includes(roleSearch.toLowerCase())
  );

  const handleNext = () => {
    if (step === 1) {
      if (alreadyAffiliatedName) {
        return;
      }
      if (!inviteEmail.trim()) {
        setError("Please enter an email address");
        return;
      }
      if (!looksLikeEmail(inviteEmail)) {
        setError("Please enter a valid email address");
        return;
      }
      setError(null);
      setStep(2);
      return;
    }
    if (step === 2) {
      if (!selectedRole.trim()) {
        setError("Please enter a role");
        return;
      }
      setError(null);
      setStep(3);
    }
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
    } else if (step === 3) {
      setStep(2);
    }
    setError(null);
  };

  /**
   * @returns {Promise<void>}
   */
  const handleSendInvitation = async () => {
    if (!inviteEmail.trim() || !selectedRole.trim() || creating || alreadyAffiliatedName) {
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const affiliationData = {
        email: inviteEmail.trim().toLowerCase(),
        org_id: userId,
        role: selectedRole.trim(),
      };
      const result = await createInviteByEmail(affiliationData);
      if (result.error) {
        setError(result.error || "Failed to send invitation");
      } else {
        if (refreshUser) {
          await refreshUser();
        }
        onOpenChange(false);
      }
    } catch (err) {
      setError("Network error while sending invitation");
    } finally {
      setCreating(false);
    }
  };

  const initial = (name) => name.replace(/\s+/g, " ").trim().slice(0, 2).toUpperCase() || "U";

  const step1 = (
    <div className="flex min-h-[min(420px,50vh)] flex-1 flex-col">
      <div className="shrink-0 border-b border-ui-border p-3 sm:p-4">
        <input
          type="search"
          placeholder="Search users…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={inputClass}
          autoFocus
        />
        <input
          type="email"
          placeholder="Or enter email address…"
          value={inviteEmail}
          onChange={(e) => setInviteEmail(e.target.value)}
          className={`${inputClass} mt-2`}
          aria-invalid={Boolean(alreadyAffiliatedName)}
          aria-describedby={alreadyAffiliatedName ? "aff-email-warning" : undefined}
        />
        {alreadyAffiliatedName ? (
          <p id="aff-email-warning" className="mt-1.5 text-sm text-error" role="alert">
            {alreadyAffiliatedName} is already affiliated with your organization.
          </p>
        ) : null}
      </div>
      {selectedUser ? (
        <div className="shrink-0 border-b border-ui-border p-3 sm:p-4">
          <div className="flex items-center gap-3 rounded-lg border border-ui-border bg-ui-muted px-3 py-2">
            <Avatar className="h-10 w-10">
              <AvatarImage
                src={selectedUser.pfp_url || defaultPfpUrl}
                alt=""
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = defaultPfpUrl;
                }}
              />
              <AvatarFallback>{initial(personLabel(selectedUser))}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="font-medium text-text-primary">{personLabel(selectedUser)}</div>
              {selectedUser.username && (selectedUser.first_name || selectedUser.last_name) ? (
                <div className="text-sm text-text-muted">@{selectedUser.username}</div>
              ) : null}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0"
              onClick={handleClearSelectedUser}
              aria-label="Clear selection"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </Button>
          </div>
        </div>
      ) : null}
      <ScrollArea className="min-h-0 flex-1">
        <div className="relative">
          {showSearchUpdating ? (
            <div
              className="pointer-events-none absolute inset-x-0 top-0 z-10 flex justify-center bg-gradient-to-b from-ui-background/95 to-transparent py-2"
              aria-live="polite"
            >
              <span className="rounded-full border border-ui-border bg-ui-card px-3 py-1 text-xs text-ui-muted-foreground shadow-sm">
                Updating…
              </span>
            </div>
          ) : null}
          {showInitialSearch ? (
            <div className="p-6 text-center text-sm text-ui-muted-foreground">Searching…</div>
          ) : null}
          {!fetching && searchQuery.trim() && searchResults.length === 0 ? (
            <div className="p-6 text-center text-sm text-ui-muted-foreground">No users found</div>
          ) : null}
          {!selectedUser && searchQuery.trim() === "" ? (
            <div className="p-6 text-center text-sm text-ui-muted-foreground">Start typing to search</div>
          ) : null}
          {searchResults.slice(0, 8).map((u) => (
            <button
              key={u.id}
              type="button"
              onClick={() => handleUserSelect(u)}
              className={cn(
                "flex w-full items-center gap-3 px-4 py-3 text-left transition-colors",
                "hover:bg-ui-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ui-ring",
                showSearchUpdating && "opacity-60"
              )}
            >
              <Avatar className="h-10 w-10 shrink-0">
                <AvatarImage
                  src={u.pfp_url || defaultPfpUrl}
                  alt=""
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = defaultPfpUrl;
                  }}
                />
                <AvatarFallback>{initial(personLabel(u))}</AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-text-primary">{personLabel(u)}</div>
                {u.username && (u.first_name || u.last_name) ? (
                  <div className="truncate text-sm text-text-muted">@{u.username}</div>
                ) : null}
              </div>
            </button>
          ))}
        </div>
      </ScrollArea>
    </div>
  );

  const step2 = (
    <div className="list-scrollbar max-h-[min(58vh,480px)] space-y-4 overflow-y-auto p-1 sm:space-y-5">
      <div>
        <h3 className="text-base font-semibold text-text-primary">Role and start date</h3>
        <p className="text-sm text-text-secondary">Assign a role and optionally a start date.</p>
      </div>
      <div>
        <label className="mb-1.5 block text-sm font-medium text-text-primary" htmlFor="aff-role">
          Role <span className="text-error">*</span>
        </label>
        <div className="relative" ref={roleDropdownRef}>
          <input
            id="aff-role"
            type="text"
            placeholder="Search or type a role"
            value={roleSearch}
            onChange={(e) => handleRoleInputChange(e.target.value)}
            onFocus={() => setShowRoleDropdown(true)}
            disabled={creating}
            className={inputClass}
          />
          {showRoleDropdown && filteredRoles.length > 0 ? (
            <div className="list-scrollbar absolute z-10 mt-1 max-h-52 w-full overflow-auto rounded-md border border-ui-border bg-ui-card py-0.5 shadow-profile-md">
              {filteredRoles.map((role) => (
                <button
                  key={role}
                  type="button"
                  onClick={() => handleRoleSelect(role)}
                  className="w-full px-3 py-2 text-left text-sm text-text-primary transition-colors hover:bg-ui-muted"
                >
                  {role}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>
      <div>
        <label className="mb-1.5 block text-sm font-medium text-text-primary" htmlFor="aff-start">
          Start date (optional)
        </label>
        <input
          id="aff-start"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          disabled={creating}
          className={inputClass}
        />
      </div>
    </div>
  );

  const step3 = (
    <div className="space-y-4">
      {selectedUser ? (
        <div className="flex items-center gap-4 rounded-lg border border-ui-border bg-ui-muted p-4">
          <Avatar className="h-16 w-16 border-2 border-ui-border">
            <AvatarImage
              src={selectedUser.pfp_url || defaultPfpUrl}
              alt=""
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = defaultPfpUrl;
              }}
            />
            <AvatarFallback className="text-lg">{initial(personLabel(selectedUser))}</AvatarFallback>
          </Avatar>
          <div>
            <div className="text-lg font-semibold text-text-primary">{personLabel(selectedUser)}</div>
            {selectedRole ? (
              <p className="mt-0.5 text-sm text-text-secondary">
                Role: <span className="font-medium text-text-primary">{selectedRole}</span>
              </p>
            ) : null}
            {startDate ? (
              <p className="mt-0.5 text-sm text-text-muted">Starts {new Date(startDate).toLocaleDateString()}</p>
            ) : null}
          </div>
        </div>
      ) : null}
      {inviteEmail ? (
        <div className="rounded-lg border border-ui-border bg-ui-card p-4 text-sm text-text-secondary">
          Invitation email: <span className="font-medium text-text-primary">{inviteEmail}</span>
        </div>
      ) : null}
      <div className="rounded-lg border border-ui-border bg-ui-card p-4">
        <p className="text-sm leading-relaxed text-text-secondary">
          The recipient will get an email with a secure invite link. If they already have an account, they can
          accept directly; otherwise they will create an account with this email.
        </p>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex min-h-[min(580px,72vh)] max-h-[min(92vh,880px)] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg">
        <div className="shrink-0 border-b border-ui-border px-4 py-3 sm:px-5 sm:py-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 text-left">
              <DialogTitle className="text-left text-lg sm:text-xl">
                {step === 1 && "Find a user or enter email"}
                {step === 2 && "Role and start date"}
                {step === 3 && "Confirm invitation"}
                <span className="ml-2 text-sm font-normal text-ui-muted-foreground">({step}/3)</span>
              </DialogTitle>
              <DialogDescription>
                {step === 1 && "Search for someone or directly invite by email."}
                {step === 2 && "Set their role. You can adjust this later for active members."}
                {step === 3 && "Review and send the invitation."}
              </DialogDescription>
            </div>
            <DialogClose asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="shrink-0 text-ui-muted-foreground"
                aria-label="Close"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </Button>
            </DialogClose>
          </div>
        </div>

        {error ? (
          <div className="shrink-0 border-b border-ui-border bg-ui-muted px-4 py-2 sm:px-5" role="alert">
            <p className="text-sm text-error">{error}</p>
          </div>
        ) : null}

        <div className="min-h-0 flex-1 overflow-hidden px-4 sm:px-5">
          {step === 1 && step1}
          {step === 2 && step2}
          {step === 3 && step3}
        </div>

        <div className="shrink-0 space-y-2 border-t border-ui-border p-3 sm:p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            {step > 1 ? (
              <Button type="button" variant="outline" onClick={handleBack} disabled={creating}>
                Back
              </Button>
            ) : (
              <span />
            )}
            {step < 3 ? (
              <Button
                type="button"
                onClick={handleNext}
                disabled={
                  creating ||
                  (step === 1 &&
                    (Boolean(alreadyAffiliatedName) || !looksLikeEmail(inviteEmail)))
                }
                className="ml-auto"
              >
                Next
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleSendInvitation}
                disabled={creating || !inviteEmail.trim() || !selectedRole.trim() || Boolean(alreadyAffiliatedName)}
                className="ml-auto"
              >
                {creating ? "Sending…" : "Send invitation"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default NewAffiliationModal;
